import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../lib/supabase';
import { HeroImage, HeroScreenSize } from '../../types/hero';
import HeroModal from './HeroModal';

interface SortableItemProps {
  id: string;
  image: HeroImage;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, image, index, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group p-4 bg-[#1F2B1F] rounded-xl"
    >
      <div className="flex items-center gap-4">
        <div {...attributes} {...listeners} className="cursor-grab">
          <GripVertical className="w-5 h-5 text-white/40" />
        </div>
        <div className="relative aspect-video w-40 overflow-hidden rounded-lg">
          <img
            src={image.image_url}
            alt={`Hero image ${index + 1}`}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1">
          <p className="text-sm text-white/70">Position: {index + 1}</p>
          <p className="text-sm text-white/70">Screen Size: {image.screen_size.toUpperCase()}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const HeroList = () => {
  const [images, setImages] = useState<HeroImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<HeroImage | null>(null);
  const [activeScreenSize, setActiveScreenSize] = useState<HeroScreenSize>('lg');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchImages();
  }, [activeScreenSize]);

  const fetchImages = async () => {
    try {
      const { data, error } = await supabase
        .from('hero_images')
        .select('*')
        .eq('screen_size', activeScreenSize)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setImages(data || []);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Failed to fetch images');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = images.findIndex((i) => i.id === active.id);
    const newIndex = images.findIndex((i) => i.id === over.id);
    
    // Create new array with updated order
    const newOrder = arrayMove(images, oldIndex, newIndex);

    try {

      // First, update all display orders to temporary values (negative numbers)
      // to avoid unique constraint conflicts
      for (let i = 0; i < newOrder.length; i++) {
        const { error: tempError } = await supabase
          .from('hero_images')
          .update({ display_order: -(i + 1) })
          .eq('id', newOrder[i].id);

        if (tempError) throw tempError;
      }

      // Then update to final values
      for (let i = 0; i < newOrder.length; i++) {
        const { error: finalError } = await supabase
          .from('hero_images')
          .update({ display_order: i })
          .eq('id', newOrder[i].id);

        if (finalError) throw finalError;
      }

      // Only update state after successful database update
      setImages(newOrder);
    } catch (err) {
      console.error('Error updating image order:', err);
      // Refetch images to ensure UI matches database state
      fetchImages();
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const { error } = await supabase
        .from('hero_images')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setImages(images.filter(img => img.id !== id));
    } catch (err) {
      console.error('Error deleting image:', err);
      alert('Failed to delete image');
    }
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Hero Images</h2>
        <button
          onClick={() => {
            setSelectedImage(null);
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Image
        </button>
      </div>

      {/* Screen Size Selector */}
      <div className="flex gap-4">
        {(['sm', 'md', 'lg'] as HeroScreenSize[]).map((size) => (
          <button
            key={size}
            onClick={() => setActiveScreenSize(size)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeScreenSize === size
                ? 'bg-[#E85C0D] text-white'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            {size.toUpperCase()} Screens
          </button>
        ))}
      </div>

      {/* Images List */}
      <div className="bg-[#1F2B1F] rounded-xl overflow-hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={images}
            strategy={verticalListSortingStrategy}
          >
            <div className="divide-y divide-white/10">
              {images.length === 0 ? (
                <div className="p-8 text-center text-white/50">No images found</div>
              ) : (
                <div className="space-y-4 p-4">
                  {images.map((image, index) => (
                    <SortableItem
                      key={image.id}
                      id={image.id}
                      image={image}
                      index={index}
                      onEdit={() => {
                        setSelectedImage(image);
                        setIsModalOpen(true);
                      }}
                      onDelete={() => handleDelete(image.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <HeroModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedImage(null);
        }}
        image={selectedImage}
        screenSize={activeScreenSize}
        onSave={fetchImages}
      />
    </div>
  );
};

export default HeroList;